import { Dispatch, SetStateAction, useMemo } from 'react';

import { ardotAttributeDictionary } from '@atom/components/common/requests/customTenantUtilities';
import { Icon, IconButton, Select } from '@atom/mui';
import { ReplayIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetDetail } from '@atom/types/asset';
import {
  ARDOTAttribute,
  AssetRequestCreateInput,
} from '@atom/types/assetRequest';
import { UserDetail } from '@atom/types/user';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import UserSearch from './UserSearch';

import classes from './ardotCreate.module.css';

const { MenuItem } = Select;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  search: {
    marginTop: '1.25rem',
  },
};

type AttributesSectionProps = {
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  asset: AssetDetail;
  selectedUser: UserDetail;
  setSelectedUser: Dispatch<SetStateAction<UserDetail>>;
};

const AttributesSection = ({
  asset,
  createInputs,
  updateCreateInput,
  selectedUser,
  setSelectedUser,
}: AttributesSectionProps) => {
  const attributeDictionary = ardotAttributeDictionary(asset);

  const updateAttribute = (name: ARDOTAttribute, value: any) => {
    const updatedAttributes = createInputs.attributes.map(attribute => {
      return attribute.name === name
        ? {
            ...attribute,
            newValue: value,
          }
        : attribute;
    });

    updateCreateInput(updatedAttributes, 'attributes');
  };

  const updateUser = (user: UserDetail) => {
    updateAttribute(ARDOTAttribute.OPERATOR_NAME, user ? user?.email : null);
    setSelectedUser(user);
  };

  const resetAttribute = (name: ARDOTAttribute) => {
    const updatedAttributes = createInputs.attributes.map(attribute => {
      return attribute.name === name
        ? {
            ...attribute,
            newValue: attribute?.oldValue,
          }
        : attribute;
    });

    updateCreateInput(updatedAttributes, 'attributes');
  };

  const getValue = (name: ARDOTAttribute) => {
    return createInputs.attributes.find(item => item.name === name)?.newValue;
  };

  const isEdited = (name: ARDOTAttribute) => {
    const attribute = createInputs.attributes.find(item => item.name === name);
    return attribute?.newValue !== attribute?.oldValue;
  };

  const getEnumeration = (name: ARDOTAttribute): string[] => {
    return attributeDictionary[name]?.enumeration || [];
  };

  // Faking user object for initial user chip display
  const initialFakeUser = isNilOrEmpty(
    attributeDictionary[ARDOTAttribute.OPERATOR_NAME]?.value,
  )
    ? null
    : {
        id: '',
        firstName: '',
        lastName: '',
        photoUrl: '',
        email: attributeDictionary[ARDOTAttribute.OPERATOR_NAME]?.value,
      };

  const editedAttributesCount = useMemo(() => {
    return createInputs.attributes.reduce((count, attribute) => {
      return attribute.newValue !== attribute.oldValue ? count + 1 : count;
    }, 0);
  }, [createInputs]);

  return (
    <>
      <div className={classes.countContainer}>
        <div
          className={classes.countTitle}
        >{`Update Attributes (${editedAttributesCount})`}</div>
        <div>Update the inventory attributes once the request is approved.</div>
      </div>
      <UserSearch
        initialValue={initialFakeUser}
        activeValue={selectedUser}
        value={[selectedUser?.id]}
        updateValue={newValue => updateUser(newValue[0])}
        label={ARDOTAttribute.OPERATOR_NAME}
        isEdited={isEdited(ARDOTAttribute.OPERATOR_NAME)}
        resetAttribute={() => resetAttribute(ARDOTAttribute.OPERATOR_NAME)}
        style={styles.search}
      />
      <div className={classes.inputRow}>
        <Select
          InputLabelProps={{ style: styles.label }}
          key={ARDOTAttribute.COMMUTE_STATUS}
          label={ARDOTAttribute.COMMUTE_STATUS}
          fullWidth
          onChange={event =>
            updateAttribute(ARDOTAttribute.COMMUTE_STATUS, event.target.value)
          }
          value={getValue(ARDOTAttribute.COMMUTE_STATUS)}
          data-cy="assetRequest118CommuteStatusSelect"
        >
          {getEnumeration(ARDOTAttribute.COMMUTE_STATUS).map(
            (option: string, index: number) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ),
          )}
        </Select>
        {isEdited(ARDOTAttribute.COMMUTE_STATUS) && (
          <div className={classes.editedControls}>
            Edited
            <IconButton
              size="small"
              onClick={() => resetAttribute(ARDOTAttribute.COMMUTE_STATUS)}
              data-cy="assetRequest118CommuteStatusResetButton"
            >
              <Icon icon={ReplayIcon} />
            </IconButton>
          </div>
        )}
      </div>
      <div className={classes.inputRow}>
        <Select
          InputLabelProps={{ style: styles.label }}
          key={ARDOTAttribute.MONITORED_STATUS}
          label={ARDOTAttribute.MONITORED_STATUS}
          fullWidth
          onChange={event =>
            updateAttribute(ARDOTAttribute.MONITORED_STATUS, event.target.value)
          }
          value={getValue(ARDOTAttribute.MONITORED_STATUS)}
          data-cy="assetRequest118MonitoredStatusSelect"
        >
          {getEnumeration(ARDOTAttribute.MONITORED_STATUS).map(
            (option: string, index: number) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ),
          )}
        </Select>
        {isEdited(ARDOTAttribute.MONITORED_STATUS) && (
          <div className={classes.editedControls}>
            Edited
            <IconButton
              size="small"
              onClick={() => resetAttribute(ARDOTAttribute.MONITORED_STATUS)}
              data-cy="assetRequest118MonitoredStatusResetButton"
            >
              <Icon icon={ReplayIcon} />
            </IconButton>
          </div>
        )}
      </div>
    </>
  );
};

export default AttributesSection;
